import Cookies from "js-cookie";
import { find } from "lodash";
// eslint-disable-next-line no-restricted-imports
import { useRouter } from "next/router";
import {
  ChangeEvent,
  FunctionComponent,
  ReactElement,
  SyntheticEvent,
  useEffect,
  useState
} from "react";
import ReCAPTCHA from "react-google-recaptcha";
import Button from "rivals/components/shared/Button";
import PasswordInput from "rivals/components/shared/Form/PasswordInput";
import FormInput from "rivals/components/shared/Form/TextInput";
import { InputType } from "rivals/components/shared/Form/TextInput/types";
import Link from "rivals/components/shared/Link";
import { PATHS } from "rivals/shared/constants";
import { setData } from "rivals/shared/utils/qa";
import { redirectToGuce } from "rivals/shared/utils/routes";
import styles from "./LoginForm.module.scss";
import strings from "./strings";
import { Props, State } from "./types";

const INVALID_REFERRERS = [
  PATHS.CREATE_ACCOUNT,
  PATHS.FORGOT_PASSWORD,
  PATHS.FORGOT_USERNAME,
  PATHS.SIGN_UP,
  PATHS.STRICT_SIGN_IN,
  PATHS.SUBSCRIBE
];

const LoginForm: FunctionComponent<Props> = ({
  authenticated,
  loginUser,
  isRedirecting,
  redirectURI,
  setIsRedirecting,
  submitting
}): ReactElement | null => {
  const router = useRouter();
  const [captchaChecked, setCaptchaChecked] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);

  // TODO: https://griosf.atlassian.net/browse/RVLS-7066
  const handleRouteChange = (): void => {
    Cookies.remove("damke");
  };

  useEffect(() => {
    addEventListener("beforeunload", handleRouteChange);

    return (): void => {
      removeEventListener("beforeunload", handleRouteChange);
    };
  }, []);

  useEffect(() => {
    if (
      Cookies.get("damke") === "true" &&
      process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY
    ) {
      setShowCaptcha(true);
    } else {
      setShowCaptcha(false);
    }
  });

  const [formValues, setFormValues] = useState<State>({
    captchaToken: null,
    login: "",
    password: ""
  });

  const validReferrer = (redirectURI: string): boolean =>
    !find([...INVALID_REFERRERS, router.pathname], path =>
      redirectURI.includes(path)
    );

  /**
   * Following login
   * If GUCE redirect is enabled
   * - Redirect to GUCE and then to referrer
   * Else
   * - Redirect to the referrer, unless the referrer is blacklisted (see INVALID_REFERRERS)
   */
  const redirect = (): void => {
    if (authenticated) {
      setIsRedirecting(true);

      const finalUri =
        redirectURI && validReferrer(redirectURI) ? redirectURI : PATHS.HOME;

      redirectToGuce(finalUri);
    }
  };

  useEffect(redirect, [authenticated]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setFormValues({
      ...formValues,
      [e.currentTarget.name]: e.currentTarget.value
    });
  };

  const handleCaptcha = (token: string | null): void => {
    if (token === null) {
      setCaptchaChecked(false);
    } else {
      setCaptchaChecked(true);
      setFormValues({
        ...formValues,
        captchaToken: token
      });
    }
  };

  const handleSubmit = (e: SyntheticEvent<HTMLFormElement>): void => {
    e.preventDefault();
    e.stopPropagation();

    const { login, password, captchaToken } = formValues;

    loginUser(login, password, captchaToken);

    if (window.grecaptcha) {
      window.grecaptcha.reset();
      setCaptchaChecked(false);
    }
  };

  const {
    buttonText,
    forgotPasswordText,
    forgotUsernameText,
    header,
    label1,
    label2
  } = strings;

  return (
    <div className={styles.container} {...setData("login-form")}>
      <h3 className={styles.header} {...setData("login-form-header")}>
        {header}
      </h3>
      <form method="post" onSubmit={handleSubmit}>
        <FormInput
          data="login-form-username-or-email"
          htmlFor="login"
          label={label1}
          onChange={handleInputChange}
          required={false}
          type={InputType.TEXT}
          value={formValues.login}
        />
        <PasswordInput
          data="login-form-password"
          label={label2}
          onChange={handleInputChange}
          value={formValues.password}
        />
        {showCaptcha && (
          <div
            className={styles.captchaContainer}
            {...setData("login-form", "captcha")}
          >
            <ReCAPTCHA
              onChange={handleCaptcha}
              sitekey={process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY || ""}
            />
          </div>
        )}
        <div
          className={
            showCaptcha
              ? styles.buttonContainerCaptcha
              : styles.buttonContainerNoCaptcha
          }
        >
          <Button
            buttonText={buttonText}
            data="login-form-submit"
            disabled={
              submitting ||
              isRedirecting ||
              (Cookies.get("damke") === "true" && !captchaChecked)
            }
            inverted
            type="submit"
          />
        </div>
        <div className={styles.linkContainer}>
          <Link data="login-form-forgot-username" href={PATHS.FORGOT_USERNAME}>
            {forgotUsernameText}
          </Link>
          <Link data="login-form-forgot-password" href={PATHS.FORGOT_PASSWORD}>
            {forgotPasswordText}
          </Link>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;
